import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { animeList, articles, categories, siteInfo } from '@/mocks/anime';

const featuredAnime = animeList.filter((a) => a.featured);
const hotAnime = animeList.filter((a) => a.rating >= 9.0).slice(0, 8);
const latestArticles = articles.slice(0, 4);
const displayCategories = categories.filter((c) => c.slug !== 'all').slice(0, 8);

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);
  const heroAnime = featuredAnime[heroIndex % featuredAnime.length];

  return (
    <>
      <ScrollToTop />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: siteInfo.name,
              url: window.location.origin,
              logo: `${window.location.origin}/logo.png`,
              description: siteInfo.description,
              email: siteInfo.email,
              telephone: siteInfo.phone,
              address: {
                '@type': 'PostalAddress',
                addressLocality: '广州',
                addressCountry: 'CN',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: siteInfo.name,
              url: window.location.origin,
              description: siteInfo.description,
              potentialAction: {
                '@type': 'SearchAction',
                target: `${window.location.origin}/search?q={search_term_string}`,
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: `${siteInfo.name} - ${siteInfo.tagline}`,
              description: siteInfo.description,
              url: window.location.origin,
              isPartOf: {
                '@type': 'WebSite',
                name: siteInfo.name,
                url: window.location.origin,
              },
            },
          ]),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20beautiful%20Japanese%20anime%20style%20cherry%20blossom%20scene%20with%20pink%20petals%20floating%20in%20spring%20air%2C%20soft%20dreamy%20atmosphere%2C%20warm%20golden%20sunlight%2C%20ethereal%20and%20romantic%20background%2C%20clean%20aesthetic%20illustration%20with%20gradient%20sky&width=1800&height=900&seq=hero-bg-01&orientation=landscape"
              alt="樱花动漫 - 好看的动漫门户网站"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>

            <div className="absolute inset-0 flex items-center">
              <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
                <div className="max-w-2xl">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-sm font-medium mb-5 backdrop-blur-sm">
                    {heroAnime.status} · {heroAnime.year}
                  </span>
                  <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight">
                    {heroAnime.title}
                  </h1>
                  <p className="text-background-200 text-sm md:text-base leading-relaxed mb-6 line-clamp-3 max-w-lg">
                    {heroAnime.description}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap mb-6">
                    {heroAnime.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-background-50/20 text-background-100 text-xs backdrop-blur-sm"
                      >
                        {tag}
                      </span>
                    ))}
                    <span className="flex items-center gap-1 text-accent-400 text-sm">
                      <i className="ri-star-fill"></i>
                      {heroAnime.rating}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/article/${heroAnime.id}`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      查看详情
                    </Link>
                    <button
                      onClick={() =>
                        setHeroIndex((prev) => prev + 1)
                      }
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/15 text-background-100 text-sm font-medium hover:bg-background-50/25 transition-colors backdrop-blur-sm whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-right-line"></i>
                      换一部
                    </button>
                  </div>

                  <div className="flex items-center gap-2 mt-8">
                    {featuredAnime.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setHeroIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                          idx === heroIndex % featuredAnime.length
                            ? 'w-6 bg-primary-400'
                            : 'bg-background-50/40 hover:bg-background-50/60'
                        }`}
                        aria-label={`切换到第${idx + 1}部推荐`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  分类浏览
                </h2>
                <Link
                  to="/category/all"
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                {displayCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                      <i className={`${cat.icon} text-lg md:text-xl`}></i>
                    </div>
                    <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Hot Recommendations */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    热门推荐
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    动漫爱好者都在看的高分作品
                  </p>
                </div>
                <Link
                  to="/category/all"
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  更多推荐
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {hotAnime.map((anime) => (
                  <Link
                    key={anime.id}
                    to={`/article/${anime.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                      <img
                        src={anime.image}
                        alt={anime.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                          {anime.rating}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                        <p className="text-background-100 text-xs line-clamp-3">
                          {anime.description}
                        </p>
                      </div>
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {anime.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-foreground-500">
                        {anime.status}
                      </span>
                      <span className="text-xs text-foreground-400">·</span>
                      <span className="text-xs text-foreground-500">
                        {anime.episodes}集
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    深度动漫分析与推荐指南
                  </p>
                </div>
                <Link
                  to="/category/all"
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  全部文章
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {latestArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors group cursor-pointer"
                  >
                    <div className="w-24 h-24 md:w-28 md:h-28 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex flex-col justify-between min-w-0">
                      <div>
                        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-500 mt-2 line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 md:py-16 bg-primary-500">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-background-50">
                    {siteInfo.totalUsers.toLocaleString()}+
                  </div>
                  <p className="text-sm text-background-200 mt-2">注册用户</p>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-background-50">
                    {siteInfo.totalArticles.toLocaleString()}+
                  </div>
                  <p className="text-sm text-background-200 mt-2">原创文章</p>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-background-50">
                    {siteInfo.dailyVisits.toLocaleString()}+
                  </div>
                  <p className="text-sm text-background-200 mt-2">日均访问</p>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-background-50">
                    {animeList.length}+
                  </div>
                  <p className="text-sm text-background-200 mt-2">精选动漫</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                加入樱花动漫，发现更多精彩
              </h2>
              <p className="text-foreground-500 text-sm md:text-base mb-6 max-w-lg mx-auto">
                与50万动漫爱好者一起，探索无限精彩的动漫世界
              </p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-rocket-line text-lg"></i>
                立即探索
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}